import { HttpClient } from '@angular/common/http';
import { ApplicationRef, ComponentFactoryResolver, Injectable, Injector, NgZone } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { Cluster } from '@googlemaps/markerclusterer';
import { svgElement } from 'src/dictionaries/svg-sign';
import { ComplementInformationEnum, ComplementInformationSignEnum, ProjectSignalingStatusEnum, SignalingTypeEnum, SystemModuleEnum, VerticalSignalingEnum, VerticalSignalizationTypeEnum } from 'src/enumerators';
import { environment } from 'src/environments/environment';
import { InfoWindowDinamicComponent } from '../components/info-window-dinamic/info-window-dinamic.component';
import { VerticalTrafficSignaling } from 'src/models';

@Injectable({
  providedIn: 'root'
})
export class MapGoogleService {
  private googleAPI = 'AIzaSyAZ_HekO0lhvxlR00enMyeH4csXhEAw-yU';
  streetPanorama: google.maps.StreetViewPanorama;
  signsMap: Array<any> = [];
  loader = new Loader({
    apiKey: this.googleAPI,
    version: "beta",
    libraries: ['marker', 'streetView', 'geometry', 'drawing']
  });
  sidebarMapOpen = false;
  sidebarType: SystemModuleEnum;
  sidebarIdSelected = '';
  sidebarSelected;
  infoWindow: google.maps.InfoWindow;
  uri = environment.uri;

  constructor(
    private _http: HttpClient,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
    private ngZone: NgZone,
  ) { }

  async createGoogleMap(id, zoom = 13, options: google.maps.MapOptions, center?) {
    if (!center) {
      const locations = JSON.parse(localStorage.getItem('mapLocations'));
      if (locations) {
        center = { lat: locations.lat, lng: locations.lng };
        zoom = locations.zoom;
      } else
        center = { lat: -15.7754462, lng: -47.7970891 };
    }
    return this.loader.load().then(async () => {
      let googleMap: google.maps.Map;
      this.infoWindow = new google.maps.InfoWindow();
      const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;
      googleMap = new Map(document.getElementById(id) as HTMLElement, {
        center,
        zoom,
        mapId: 'e37e46f2e58fad6f',
        gestureHandling: (navigator.userAgent.includes('Mobile')) ? 'cooperative' : 'greedy',
        ...options,
      })
      return googleMap
    })
  }

  setStreetViewOptions(map) {
    map.getStreetView().set('fullscreenControl', false);
    map.getStreetView().set('addressControlOptions', {
      position: google.maps.ControlPosition.TOP_RIGHT,
    });
  }

  createDrawManager(): google.maps.drawing.DrawingManager {
    let drawingManager = new google.maps.drawing.DrawingManager({
      drawingControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
        drawingModes: [
          google.maps.drawing.OverlayType.POLYLINE,
          google.maps.drawing.OverlayType.POLYGON,
        ],
      },
      drawingControl: false,
      polygonOptions: {
        editable: true,
        fillColor: '#00c714',
        fillOpacity: 0.3,
        strokeColor: '#00c714',
      },
      polylineOptions: {
        editable: true,
        strokeColor: '#00c714',
        strokeWeight: 3,
      },
    });

    return drawingManager;
  }


  // Caso não tenha localização tento pegar nessa ordem,
  // local do navegador, local que está salvo no storage, fixo atman
  async getLocation(): Promise<google.maps.LatLngLiteral> {
    return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (error) => {
            console.error("Erro ao obter localização:", error);

            // Tenta buscar a localização padrão do localStorage
            const defaultLocation = JSON.parse(localStorage.getItem('mapLocations') || '{}');
            resolve(defaultLocation?.lat && defaultLocation?.lng
              ? { lat: defaultLocation.lat, lng: defaultLocation.lng }
              : { lat: -15.7754462, lng: -47.7970891 });
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
          }
        );
      } else {
        console.error("Geolocation não é suportada neste navegador.");
        // Define a localização padrão a partir do localStorage ou usa um valor fixo
        const defaultLocation = JSON.parse(localStorage.getItem('mapLocations') || '{}');
        resolve(defaultLocation?.lat && defaultLocation?.lng
          ? { lat: defaultLocation.lat, lng: defaultLocation.lng }
          : { lat: -15.7754462, lng: -47.7970891 });
      }
    });
  }

  // Cria um marcador com base apenas no codigo da sinalização
  async addGoogleAdvanceMarkerSignCode(code, map, latlng, draggable) {
    let marker: google.maps.marker.AdvancedMarkerElement;
    let elementHTML;
    // Caso seja conjugada, as 2 e 3 vão retornar undefined
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
    let contentHTML = this.contentMarkerHTMLSimple(svgElement.Signaling[code]);
    if (contentHTML) elementHTML = this.createElementFromHTML(contentHTML)

    marker = new AdvancedMarkerElement({
      position: { lat: latlng.lat, lng: latlng.lng },
      map: map,
      gmpDraggable: draggable,
      content: elementHTML,
    });

    return marker
  }

  // Adiciona o marcador no mapa com o icone de uma sinalização
  async addGoogleAdvanceMarkerSignaling(options: { sign?: any, map?: any, draggable?: boolean, pin?: boolean, socket?: boolean }): Promise<any> {
    let marker: google.maps.marker.AdvancedMarkerElement;
    let elementHTML;
    // Caso seja conjugada, as 2 e 3 vão retornar undefined
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
    let contentHTML = this.createContentMarkerHTML(options);
    let lat, lng;
    lat = options?.sign?.type == SignalingTypeEnum.Vertical ? options.sign?.lat : options?.sign?.locations[0].lat;
    lng = options?.sign?.type == SignalingTypeEnum.Vertical ? options?.sign?.lng : options?.sign?.locations[0].lng;
    if (contentHTML) elementHTML = this.createElementFromHTML(contentHTML)

    marker = new AdvancedMarkerElement({
      position: { lat, lng },
      map: options?.map,
      gmpDraggable: options?.draggable,
      content: elementHTML,
    });

    if (options?.socket) {
      const infoWindow = new google.maps.InfoWindow({
        content: `
                <div style="padding: 4px;">
                  <h3 style="margin: 0; font-size: 14px">NOVA SINALIZAÇÃO</h3>
                  <style>
                    .gm-style-iw-chr {
                      display: none !important;
                    }
                  </style>
                </div>
              `,
      });

      // Tempo para animação acabar
      setTimeout(() => {
        infoWindow.open({
          anchor: marker,
          map: options?.map,
        });
        // Retirar o popup de 'Nova Sinalização'
        setTimeout(() => {
          infoWindow.close();
        }, 5000)
      }, 1500);
    }
    return marker
  }

  // Transforma string em elemento HTML
  createElementFromHTML(htmlString) {
    var div = document.createElement('div');
    div.innerHTML = htmlString.trim();
    return div;
  }

  createContentMarkerHTML(options: { sign?: any, pin?: boolean, socket?: boolean }) {
    let contentMarkerHTML;

    if (!options?.sign) contentMarkerHTML = this.contentMarkerHTMLDefault();
    else contentMarkerHTML = this.contentMarkerHTML({ sign: options?.sign, pin: options?.pin, socket: options.socket })

    return contentMarkerHTML;
  }

  async updateContentMarkerHTML(sign, marker: google.maps.marker.AdvancedMarkerElement, pin?) {
    let contentHTML = this.createContentMarkerHTML({ sign, pin: pin });
    let elementHTML;

    if (contentHTML) elementHTML = this.createElementFromHTML(contentHTML)
    marker.content = elementHTML;
  }

  contentMarkerHTMLDefault() {
    let divHTML = `<div style="height: 78px">
      <img src="assets/img/pinSign.svg">
      <div class="position-relative" style="top: -70px; left: 14px;">
        <div style="height: 32px; width: 32px; top: 12px; left: 18px;background-color: #7E7E80; border-radius: 50%">
        </div>
      </div>
    </div>`;
    return divHTML;
  }

  contentMarkerHTML(options: { sign?: any, pin?: boolean, socket?: boolean }) {
    let divHTML;
    let classSocket = '';
    if (options?.socket) classSocket = 'new-element';
    if (options?.pin) {
      divHTML =
        `<div class='${classSocket}' style="height: 78px; display: flex; align-items: center; justify-content: center">
            <img src="assets/img/pinSign.svg">
            <div class="position-absolute" style="top: 12px;">
              <div class="marker-background-image"
              style="background-image:url(${svgElement.Signaling[options?.sign?.signCode]}); width: 40px; height: 40px;">
              </div>
            </div>
          </div>`;

    } else {
      if (options?.sign?.type == SignalingTypeEnum.Vertical) {
        divHTML = this.contentMarkerVertical(options?.sign);

      } else if (options?.sign?.type == SignalingTypeEnum.Horizontal) {
        divHTML = this.contentMarkerHorizontal(options?.sign);

      } else if (options?.sign?.type == SignalingTypeEnum.Device) {
        divHTML = this.contentMarkerDevice(options?.sign);

      }
    }
    return divHTML;
  }

  contentMarkerVertical(sign) {
    let divHTML;
    sign = new VerticalTrafficSignaling(sign);
    const captionInternalList = ['R-14', 'R-15', 'R-16', 'R-17', 'R-18', 'R-19', 'A-37', 'A-38', 'A-46', 'A-47', 'A-48']; // Sinalizações que mostram a legenda interna

    const busStop = sign.signCode == VerticalSignalingEnum.BusStop ? 'background-size: contain; background-repeat: no-repeat;' : ''; // Apenas para a sinalização de parada de ônibus
    const link = sign.combined
      ? `<img src="assets/img/linkConjugate.svg" style="width:20px; height:20px; position: absolute; left: 24px; top: -12px">`
      : ''; // Para por o círculo de conjugada
    ; // Caso tenha inforamações complementares, o SVG complementar aparece
    const occurrenceIcon = !sign?.occurrence?.id ? '' :
      `<i class="fa-solid fa-message-exclamation fa-lg occurrence-map-icon"></i>`;

    const imgComponent = this.setComponentImage(sign)

    if (imgComponent) {

      const internalCaptionSvg = `${sign?.signCode}-32`;
      divHTML =
        `<div class="d-flex justify-content-center position-relative" style="height: 60px; width: 40px;">
          ${imgComponent}
          <div
            class="position-relative internal-caption ${internalCaptionSvg}" style="height: 32px; width: 32px;">
            <span class="complement-marker-span" style="z-index: 1;">
              ${(captionInternalList.find((v) => sign?.signCode == v)) ? sign?.internalCaption : ''}
            </span>
            <div class="marker-background-image" style="background-image:url(${svgElement.Signaling[sign?.signCode]}); width: 32px; height: 32px; ${busStop}"></div>
          </div>${link}
        </div>`;
    } else {
      const iconSize = sign?.signCode == VerticalSignalingEnum.ViaFiscalizadaPorCameras || sign?.signCode == VerticalSignalingEnum.QrCode ? { w: 40, h: 60 } : { w: 40, h: 40 };
      divHTML =
        `<div class= "internal-caption ${sign.signCode}-40-map position-relative">
        <span style="z-index: 1000">${((captionInternalList.find((c) => sign?.signCode == c)) ? sign?.internalCaption : '')}</span>
        <div class="marker-background-image" style="background-image:url(${svgElement.Signaling[sign?.signCode]}); width: ${iconSize.w}px; height: ${iconSize.h}px; ${busStop}"></div>${link}
        ${occurrenceIcon}
      </div>`;
    }

    return divHTML;
  }

  contentMarkerHorizontal(sign) {
    const occurrenceIcon = !sign?.occurrence?.id ? '' :
      `<i class="fa-solid fa-message-exclamation fa-lg occurrence-map-icon"></i>`;

    const divHTML =
      `<div class= "position-relative">
      <div class="marker-background-image" style="background-image:url(${svgElement.Signaling[sign?.signCode]}); width: 40px; height: 40px;"></div>
      ${occurrenceIcon}
    </div>`;
    return divHTML;
  }

  contentMarkerDevice(sign) {
    const occurrenceIcon = !sign?.occurrence?.id ? '' :
      `<i class="fa-solid fa-message-exclamation fa-lg occurrence-map-icon"></i>`;

    const divHTML =
      `<div class= "position-relative">
      <div class="marker-background-image" style="background-image:url(${svgElement.Signaling[sign?.signCode]}); width: 40px; height: 40px;"></div>
      ${occurrenceIcon}
    </div>`;
    return divHTML;
  }

  contentMarkerHTMLSimple(svg) {
    const divHTML =
      `<div class="marker-background-image"
            style="background-image:url(${svg}); width: 40px; height: 40px;">
          </div>`;

    return divHTML;
  }

  // Retorna qual é o tipo da informação complementar sabendo qual é o tipo da placa WARNING/REGULATION
  complementType(sign): ComplementInformationSignEnum {
    if (sign?.complementInformationType == ComplementInformationEnum.Additional) {
      if (sign.verticalType == VerticalSignalizationTypeEnum.Warning)
        return ComplementInformationSignEnum.AdditionalWarning;

      else if (sign.verticalType == VerticalSignalizationTypeEnum.Regulation)
        return ComplementInformationSignEnum.AdditionalRegulation;

      else return undefined;

    } else if (sign?.complementInformationType == ComplementInformationEnum.Incorporated) {
      if (sign.verticalType == VerticalSignalizationTypeEnum.Warning)
        return ComplementInformationSignEnum.IncorporatedWarning;

      else if (sign.verticalType == VerticalSignalizationTypeEnum.Regulation)
        return ComplementInformationSignEnum.IncorporatedRegulation;

      else return undefined;

    } else return undefined;

  }

  setComponentImage(sign) {

    const complementSignType: ComplementInformationSignEnum = this.complementType(sign)
    if (!complementSignType) return '';

    const complementSvg = svgElement.ComplementInformation[complementSignType];

    const classComplement = sign?.complementInformationType == ComplementInformationEnum.Additional ? 'marker-complement-additional' : 'marker-complement-incorporated';
    const h = sign?.complementInformationType == ComplementInformationEnum.Additional ? 20 : 60;
    const b = sign?.complementInformationType == ComplementInformationEnum.Additional ? 8 : 5;
    return `<div class="${classComplement}" style="background-image:url(${complementSvg});
              width: 40px; height: ${h}px; position: absolute; bottom: ${b}px;">
            </div>`;

  }

  getCustomClusterRenderer(): any {
    return {
      render: (cluster: Cluster) => {
        const count = cluster.count;
        const div = this.createStyledClusterElement(count);  // Usa a função para criar o estilo
        return new google.maps.marker.AdvancedMarkerElement({
          position: cluster.position,
          content: div
        });
      },
    };
  }

  createStyledClusterElement(count) {
    let size = 30;
    if (count < 10) {
      size = 30;
    } else if (count < 50) {
      size = 40;
    } else {
      size = 50;
    }
    const div = document.createElement("div");
    div.innerHTML = `
      <img src="../../assets/signalings/clusteres.svg" width="${size}" height="${size}" />
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 12px;
        font-weight: bold;
        color: white;">
        ${count}
      </div>
    `;


    return div;
  }


  // Retorna lat lng com input de texto, serve para pesquisa no mapa
  mapSearch(searchInput: string, provider: 'openStreet' | 'googleMaps') {
    let url = '';

    switch (provider) {
      case 'googleMaps':
        url = 'https://maps.googleapis.com/maps/api/geocode/json';
        url += `?key=${this.googleAPI}`;
        url += `&address=${searchInput}`;
        break;

      case 'openStreet':
        url = 'https://nominatim.openstreetmap.org/search?format=json';
        url += `&q=${searchInput}`;
        url += '&countrycodes=BR';
        break;
    }

    return this._http.get(url);
  }

  getConjuntoGeosin() {
    return this._http.get('../../assets/geoBoundaries.geojson');
  }

  createAreaPolygon(area, options) {
    return new google.maps.Polygon({
      paths: area.vertices,
      strokeColor: '#b3d7e1',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#59a8be',
      fillOpacity: 0.45,
      zIndex: options.zIndex,
    });
  }

  createRegionPolygon(region, options) {
    return new google.maps.Polygon({
      paths: region.vertices,
      strokeColor: '#00799b',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#59a8be',
      fillOpacity: 0.45,
      zIndex: options.zIndex,
    });
  }

  // Criar o marcador de tarefas no mapa
  async addTaskMarkerOnMap(latlng: [number, number], color) {
    let markIcon = 'assets/icons/map-task-pin.svg';
    let width = 48;
    let height = 56;
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

    const iconStyle = `--fa-primary-color: ${color}; --fa-secondary-color: white; --fa-secondary-opacity: 1.0;`

    const divHTML =
      `<div class="d-flex align-items-center justify-content-center">
          <img height="${height}" width="${width}" src="${markIcon}">
          <span class="position-absolute d-flex align-items-center justify-content-center" style="margin-bottom: 10px">
            <i class="fa-solid fa-circle position-absolute fa-3x" style="color: ${color};"></i>
            <i class="fa-duotone fa-clipboard-list-check position-absolute fa-xl pb-1" style="${iconStyle}"></i>
        </span>
      </div>`
    let elementHTML;
    if (divHTML) elementHTML = this.createElementFromHTML(divHTML)

    let marker = new AdvancedMarkerElement({
      position: new google.maps.LatLng(latlng[0], latlng[1]),
      content: elementHTML,
    });
    return marker;
  }

  // Marcador usado ao pesquisar no mapa
  markerSearchCreate(latlng: [number, number], map: google.maps.Map) {
    // 1️⃣ Criação do HTML do marcador (você pode personalizar conforme precisar)
    const divHTML = document.createElement('div');
    divHTML.innerHTML = `<img style="width: 48px; height: 78px;" src="assets/icons/marker-search.svg">`;
    divHTML.classList.add('marker-map');

    // 2️⃣ Criação do AdvancedMarkerElement com a posição e o conteúdo personalizado
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: new google.maps.LatLng(latlng[0], latlng[1]), // posição do marcador
      map: map, // mapa onde o marcador será exibido
      content: divHTML // conteúdo personalizado do marcador
    });

    return marker;
  }

  async createInfoWindowContent(element: any, type: SystemModuleEnum,): Promise<HTMLElement> {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(InfoWindowDinamicComponent);
    const componentRef = componentFactory.create(this.injector);

    componentRef.instance.element = element;
    componentRef.instance.type = type;

    this.appRef.attachView(componentRef.hostView);

    this.ngZone.run(() => {
      componentRef.instance.element = element;
      componentRef.instance.type = type;
    });

    // componentRef.changeDetectorRef.detectChanges();
    componentRef.onDestroy(() => this.appRef.detachView(componentRef.hostView));
    return componentRef.location.nativeElement;
  }

  toggleSideBar(open = true) {
    this.sidebarMapOpen = open;
  }

  addProjectSignalingMarkerGoogle(
    signaling: any,
    type: SignalingTypeEnum,
    map: google.maps.Map = undefined
  ) {

    const svgPath = svgElement.Signaling[signaling.signCode];
    const opacityStyle = signaling.status === ProjectSignalingStatusEnum.InProject ? 'opacity: 0.5;' : '';
    const grayscaleStyle = signaling.status === ProjectSignalingStatusEnum.InTask ? 'filter: grayscale(100%);' : '';

    const divHTML = `<div style="width: 40px; height: 40px; ${opacityStyle}; ${grayscaleStyle}"><img src="${svgPath}" width="40" height="40"></div>`;

    let lat: number | undefined;
    let lng: number | undefined;

    if (type !== SignalingTypeEnum.Vertical) {
      lat = Number(signaling?.locations[0]?.latitude);
      lng = Number(signaling?.locations[0]?.longitude);
    } else {
      lat = Number(signaling.latitude);
      lng = Number(signaling.longitude);
    }

    if (lat && lng && signaling.status !== ProjectSignalingStatusEnum.InCatalog) {
      const markerElement = document.createElement('div');
      markerElement.innerHTML = divHTML;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        content: markerElement,
        map,
        title: `${type.toUpperCase()} - ${signaling.code}`
      });
      return marker;
    }
  }

  addTaskActivityMarker(
    activity: any,
    type: SignalingTypeEnum,
    map: google.maps.Map = undefined
  ): google.maps.marker.AdvancedMarkerElement | undefined {

    // 1. Normalização dos Dados
    // Precisamos extrair o código da placa e o código do projeto.
    // Como os nomes das propriedades mudam dependendo do tipo, usamos um switch ou mapa.
    let signCode: string;
    let projectCode: string;

    switch (type) {
      case SignalingTypeEnum.Vertical:
        signCode = activity.verticalCode;
        projectCode = activity.projectTrafficSign?.code;
        break;
      case SignalingTypeEnum.Horizontal:
        signCode = activity.horizontalCode;
        projectCode = activity.projectHorizontalSignaling?.code;
        break;
      case SignalingTypeEnum.Device:
        signCode = activity.deviceCode;
        projectCode = activity.projectDeviceSignaling?.code;
        break;
      default:
        console.warn('Tipo de atividade desconhecido:', type);
        return undefined;
    }

    // Validação básica
    if (!signCode || !activity.latitude || !activity.longitude) return undefined;

    // 2. Construção do Elemento Visual (DOM)
    const svgPath = svgElement.Signaling[signCode];
    const div = document.createElement("div");

    // Estilos inline para performance e simplicidade
    div.style.width = "40px";
    div.style.height = "40px";
    div.style.filter = "grayscale(100%)";
    div.style.opacity = "0.8"; // Ajustei para 0.8 (0.5 fica muito apagado, mas pode voltar se preferir)

    // Inserção da imagem via innerHTML (mais limpo que create element img + append)
    div.innerHTML = `<img src="${svgPath}" style="width: 100%; height: 100%;">`;

    // 3. Criação do Marcador Avançado
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: Number(activity.latitude), lng: Number(activity.longitude) },
      content: div,
      map: map,
      title: `${type.toUpperCase()} - ${signCode}` // Acessibilidade/Hover nativo
    });

    return marker;
  }

  // Cria um marcador de veiculo
  async addGoogleAdvanceMarkerTeamVehicle(teamVehicle, isVehicle = false, angle?) {
    let marker: google.maps.marker.AdvancedMarkerElement;
    let position: { lat: number, lng: number };
    let elementHTML;
    // Caso seja conjugada, as 2 e 3 vão retornar undefined
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
    this.infoWindow = new google.maps.InfoWindow();
    position = { lat: parseFloat(teamVehicle?.location?.latitude), lng: parseFloat(teamVehicle?.location?.longitude) };
    let contentHTML;

    if (!isVehicle) contentHTML = this.createContentTeam(teamVehicle);
    else contentHTML = this.createContentVehicle(angle);

    if (contentHTML) elementHTML = this.createElementFromHTML(contentHTML);
    marker = this.createGoogleTeamVehicleMarker(elementHTML, position, AdvancedMarkerElement, teamVehicle);
    return marker;
  }

  createGoogleTeamVehicleMarker(elementHTML, position, AdvancedMarkerElement, team?, map?): google.maps.marker.AdvancedMarkerElement {
    if (elementHTML) {
      let marker: google.maps.marker.AdvancedMarkerElement = new AdvancedMarkerElement({
        position,
        content: elementHTML,
      });


      return marker;
    }
  }

  createContentVehicle(angle) {
    let icon =
      `<img src="assets/img/vehicle-horizontal.svg" style="width: 76px; height: 36px; transform: rotate(${-angle}deg); transform-origin: center">`

    let divHTML = `
        <div class= "position-relative">
          ${icon}
        </div>
        `;
    return divHTML;
  }

  createContentTeam(team) {
    const imgRoundedTeam = team?.leaderMember?.user?.imageURL ? team?.leaderMember?.user?.imageURL : 'assets/icons/camera.svg';
    const imgTeam = team?.teamId ?
      `<img class="image-team-rounded" src=${imgRoundedTeam} alt="">`
      : '';
    const markIcon = 'assets/icons/map-team-pin.svg';
    const divHTML = `<div class="d-flex align-items-center justify-content-center">
                        <img height="70" width="54" src="${markIcon}">
                        <span class="position-absolute d-flex align-items-center justify-content-center" style="margin-bottom: 12px">
                        ${imgTeam}
                        </span>
                      </div>`
    return divHTML;
  }

  // Cria um marcador de retrospectiva (retro marker)
  async addGoogleAdvanceMarkerRetro(latlng: number[]): Promise<google.maps.marker.AdvancedMarkerElement> {
    let marker: google.maps.marker.AdvancedMarkerElement;
    let position: { lat: number, lng: number };
    let elementHTML: HTMLElement;
    let contentHTML: string;

    // Importa a biblioteca AdvancedMarkerElement, se ainda não estiver carregada
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

    // Converte o array latlng[lat, lng] para o formato de objeto do Google Maps
    position = { lat: latlng[0], lng: latlng[1] };

    // Cria o conteúdo HTML específico para o marcador retro
    contentHTML = this.createContentRetro();

    if (contentHTML) elementHTML = this.createElementFromHTML(contentHTML);

    // Chama a função genérica para criar o marcador
    marker = this.createGoogleRetroMarker(elementHTML, position, AdvancedMarkerElement);

    return marker;
  }

  // Cria o conteúdo HTML para o marcador de retrospectiva
  createContentRetro(): string {
    let icon: string;

    // Lógica original de largura para escolher o ícone grande ou minimizado

    icon =
      `<div style="height: 60px">
        <img src="assets/img/pinSign.svg" style="width: 40px; height: 60px">
        <div class="position-relative" style="top: -80px">
          <div class="position-relative" style="height: 32px; width: 32px; top: 25px; left: 5px;">
            <img src="assets/img/retro-marker.svg" style="width: 20px; height: 20px; position: absolute;">
          </div>
        </div>
      </div>`;

    let divHTML = `
    <div class= "position-relative">
      ${icon}
    </div>
    `;

    return divHTML;
  }

  // Função auxiliar que cria o AdvancedMarkerElement
  createGoogleRetroMarker(elementHTML: HTMLElement, position: { lat: number, lng: number }, AdvancedMarkerElement: any): google.maps.marker.AdvancedMarkerElement {

    if (elementHTML) {
      let marker: google.maps.marker.AdvancedMarkerElement = new AdvancedMarkerElement({
        position: {
          lat: Number(position.lat),
          lng: Number(position.lng)
        },
        content: elementHTML,
        // Você pode querer ajustar o 'anchor' (ponto de ancoragem) para o pino
        // O Leaflet usava iconAnchor: [w / 2, h], o que coloca a base do ícone no lat/lng.
        // Isso pode ser simulado no Google Maps usando um 'anchor'
        // anchor: new google.maps.Point(w / 2, h), // Requer o tamanho w e h
      });
      return marker;
    }
  }

  addPathLayer(polylineCoordinates: any[], highLight?: boolean, colorType?: number): google.maps.Polyline {
    // Lógica de Cor (Refatorada para remover a duplicação do if/else original)
    let strokeColor = '#00799b'; // Default

    if (highLight && !colorType) {
      strokeColor = '#004659'; // Azul (Highlight)
    } else if (colorType) {
      strokeColor = (colorType === 1) ? '#FF8515' : '#D30E18'; // Laranja ou Vermelho
    }

    const polyline = new google.maps.Polyline({
      path: polylineCoordinates, // Certifique-se que é [{lat, lng}, ...]
      strokeColor: strokeColor,
      strokeOpacity: 1.0,
      strokeWeight: highLight ? 7 : 5,
      zIndex: highLight ? 1000 : 3, // Trechos geralmente ficam acima de rotas e regiões
      clickable: true,
      map: null // Não adiciona ao mapa ainda, o handlePaths gerencia isso
    });

    return polyline;
  }

  addPopupPathMarker(pathMarker) {
    let content = `
      <div class="d-flex flex-column align-items-center" style="width: 144px">
        <div style="height: 24px;">
          <h4 style="color:var(--gray-base)">${pathMarker.pathName}</h4>
        </div>
        <hr class="my-2" style="width: 100%; height: 1px">
        <div class="d-flex flex-column align-items-center">
        <h3 style="color:var(--gray-base)">KM</h3>
        <h1>${pathMarker.kilometer}</h1>
        </div>
        <hr class="my-2" style="width: 100%; height: 1px">
        <div>
          <h3 style="color:var(--gray-base)">${pathMarker.pathState}</h3>
        </div>
      </div>
    `;
    return content;
  }

  addPathMarker(
    internalCaption: string,
    state: string = '',
    latlng: google.maps.LatLngLiteral, // Aceita os dois formatos
    options?: any
  ): google.maps.marker.AdvancedMarkerElement {

    // 1. Lógica de construção das Strings (Mantida igual, apenas limpeza)

    const iconHTML = `<img src="assets/signalings/pathMarker.svg" style="width: 40px; height: 63px; position: absolute; top: 0; left: 0;">`;
    const spanInternalCaption = internalCaption !== '' ? `<span class="path-internal"> ${internalCaption} </span>` : '';
    const spanState = state ? `<span class="path-state"> ${state} </span>` : '';

    // 2. Criação do Elemento DOM (Obrigatório para AdvancedMarkerElement)
    const container = document.createElement('div');

    // Adiciona as classes
    let classes = 'marker-map position-relative path-marker-internal';
    container.className = classes;

    // Define tamanho exato para garantir alinhamento
    container.style.width = `40px`;
    container.style.height = `63px`;

    // IMPORTANTE: Correção de Âncora (Anchor)
    // O Leaflet ancora no [width/2, height] (base central).
    // O Google Maps AdvancedMarker ancora no CENTRO do elemento.
    // Para a ponta do pino ficar na coordenada, subimos o elemento em 50% da altura.
    // Mas como é um container relativo, geralmente o Google lida bem se o SVG ocupar tudo.
    // Se o pino ficar "flutuando", adicione: container.style.transform = 'translateY(-50%)';

    container.innerHTML = `
        ${iconHTML}
        ${spanInternalCaption}
        ${spanState}
    `;

    // 3. Normalização da Posição
    const position = { lat: Number(latlng.lat), lng: Number(latlng.lng) };

    // 4. Instância do Marcador
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: undefined, // Adiciona ao mapa (ou null)
      position: position,
      content: container,
    });

    return marker;
  }


}
